// Import necessary libraries and components
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Map, Source, Layer } from "react-map-gl/mapbox";
import * as turf from "@turf/turf";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  InboxOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  message,
  Upload,
  Button,
  Modal,
  Form,
  Input,
  Drawer,
  Table,
  Space,
  Popconfirm,
  Card,
  InputNumber,
  Row,
  Col,
} from "antd";
import shp from "shpjs";
import shpwrite from "@mapbox/shp-write";
import FileSaver from "file-saver";
import "../../styles/admin/admin-dashboard.css";
import "../../styles/admin/admin-tables.css";
import "../../styles/admin/upload-simfix.css";
import { generateJSON } from "../../apis";

const { Dragger } = Upload;
const { TextArea } = Input;

const UploadSimfix = () => {
  const [shapefileData, setShapefileData] = useState(null);
  const [originalShapefileData, setOriginalShapefileData] = useState(null); // Store original data for reset
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [attributeToUpdate, setAttributeToUpdate] = useState("");
  const [updateForm] = Form.useForm();
  const [viewState, setViewState] = useState({
    longitude: 12.4964,
    latitude: 41.9028,
    zoom: 10,
  });
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [featureProperties, setFeatureProperties] = useState({});
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [isBatchUpdateModalVisible, setIsBatchUpdateModalVisible] =
    useState(false);
  const [batchUpdateForm] = Form.useForm();
  const [newPropertyForm] = Form.useForm();
  const [isAddPropertyModalVisible, setIsAddPropertyModalVisible] =
    useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [mapKey, setMapKey] = useState(0); // Force map re-render

  // Location adjustment state
  const [locationOffset, setLocationOffset] = useState({ lat: 0, lng: 0 });
  const [stepSize, setStepSize] = useState(0.001); // Default step size for movement

  // JSON Generator form state
  const [sqliteFileData, setSqliteFileData] = useState(null);
  const [renameScenarios, setRenameScenarios] = useState({
    "SDF - Mattina": "SDF - *mattina",
    "Progetto 10 - Mattina (2025)": "Progetto 10 (2025) - *mattina",
    "Progetto 3 - Mattina": "Progetto 3 - *mattina",
    "Progetto 7 - Sera": "Progetto 7 - *sera",
    "Progetto - Mattina": "Progetto - *mattina",
    "SR1 - sera": "SR1 - *sera",
    "Progetto 8 - Mattina": "Progetto 8 - *mattina",
    "Progetto 7 - Mattina": "Progetto 7 - *mattina",
    "Progetto 8 - Sera": "Progetto 8 - *sera",
    "SR1 - Mattina (con bretella rotatoria)":
      "SR1 (con bretella rotatoria) - *mattina",
    "SR1 - Sera (con bretella rotatoria) ":
      "SR1 (con bretella rotatoria) - *sera",
    "Progetto SA4 - Sera": "Progetto SA4 - *sera",
    "Progetto SA2 - Mattina": "Progetto SA2 - *mattina",
    "Progetto SA2 - Sera": "Progetto SA2 - *sera",
    "Progetto SA3 - Sera": "Progetto SA3 - *sera",
    "Progetto SA4 - Mattina": "Progetto SA4 - *mattina",
    "Progetto SA3 - Mattina": "Progetto SA3 - *mattina",
    "SR2 - Mattina (senza rotatoria)": "SR2 (senza rotatoria) - *mattina",
    "Progetto SA1 - Mattina (Quadrilatero)(con rotatoria)":
      "Progetto SA1 (Quadrilatero)(con rotatoria) - *mattina",
    "Progetto SA1 - Sera (Quadrilatero) ":
      "Progetto SA1 (Quadrilatero) - *sera",
    "SR2 - Sera (senza rotatoria) ": "SR2 (senza rotatoria) - *sera",
    "Progetto SA1 - Sera (Quadrilatero) - con semaforo":
      "Progetto SA1 (Quadrilatero) - con semaforo - *sera",
    "Progetto SA1 - Mattina (Quadrilatero)(con semaforo)":
      "Progetto SA1 (Quadrilatero)(con semaforo) - *mattina",
  });
  const [vehicleLengths, setVehicleLengths] = useState({
    Car: 4,
    BRT: 12,
    Treno: 50,
    Truck: 8,
    Motorbike: 2,
    "Bus 18 m": 18,
    LGV: 6,
    "Car interscambio": 4,
    "Bus 12 m": 12,
    "Bus 18 m Assi": 18,
  });
  const [isJsonGeneratorModalVisible, setIsJsonGeneratorModalVisible] =
    useState(false);
  const [jsonGeneratorForm] = Form.useForm();
  const [isGeneratingJSON, setIsGeneratingJSON] = useState(false);

  const mapRef = useRef();

  // Function to translate coordinates by offset
  const translateCoordinates = (coordinates, latOffset, lngOffset) => {
    if (Array.isArray(coordinates[0])) {
      // Multi-dimensional array (LineString, Polygon, etc.)
      return coordinates.map((coord) =>
        translateCoordinates(coord, latOffset, lngOffset)
      );
    } else {
      // Single coordinate pair [lng, lat]
      return [coordinates[0] + lngOffset, coordinates[1] + latOffset];
    }
  };

  // Function to apply offset to all features
  const applyLocationOffset = (data, latOffset, lngOffset) => {
    if (!data || !data.features) return data;

    const translatedData = {
      ...data,
      features: data.features.map((feature) => ({
        ...feature,
        geometry: {
          ...feature.geometry,
          coordinates: translateCoordinates(
            feature.geometry.coordinates,
            latOffset,
            lngOffset
          ),
        },
      })),
    };

    return translatedData;
  };

  // Function to move shapefile in a specific direction
  const moveShapefile = (direction) => {
    if (!originalShapefileData) return;

    let newOffset = { ...locationOffset };

    switch (direction) {
      case "up":
        newOffset.lat += stepSize;
        break;
      case "down":
        newOffset.lat -= stepSize;
        break;
      case "left":
        newOffset.lng -= stepSize;
        break;
      case "right":
        newOffset.lng += stepSize;
        break;
      default:
        return;
    }

    setLocationOffset(newOffset);

    // Apply the cumulative offset to the original data
    const translatedData = applyLocationOffset(
      originalShapefileData,
      newOffset.lat,
      newOffset.lng
    );
    setShapefileData(translatedData);
    setHasUnsavedChanges(true);

    // Force map re-render
    setMapKey((prev) => prev + 1);
  };

  // Function to reset shapefile to original position
  const resetShapefilePosition = () => {
    if (!originalShapefileData) return;

    setLocationOffset({ lat: 0, lng: 0 });
    setShapefileData(originalShapefileData);
    setHasUnsavedChanges(true);

    // Force map re-render
    setMapKey((prev) => prev + 1);

    message.success("Shapefile position reset to original location");
  };

  // Function to apply manual offset values
  const applyManualOffset = (latOffset, lngOffset) => {
    if (!originalShapefileData) return;

    const newOffset = { lat: latOffset, lng: lngOffset };
    setLocationOffset(newOffset);

    const translatedData = applyLocationOffset(
      originalShapefileData,
      latOffset,
      lngOffset
    );
    setShapefileData(translatedData);
    setHasUnsavedChanges(true);

    // Force map re-render
    setMapKey((prev) => prev + 1);
  };

  // Function to handle file uploads
  const handleFileUpload = async (info, fileType) => {

    if (fileType === "shapefile") {
      try {
        const file = info.file;

        if (!file) {
          console.error("No file provided");
          return;
        }

        if (!file.name.endsWith(".zip")) {
          message.error(
            "Il file deve essere un archivio ZIP contenente lo shapefile"
          );
          return;
        }

        // Read shapefile as ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();

        const geojson = await shp(arrayBuffer);


        if (!geojson) {
          throw new Error(
            "Shapefile parsing returned null or undefined. The ZIP file might not contain valid shapefile data."
          );
        }

        // Handle case where shpjs returns an array of FeatureCollections
        let finalData = geojson;
        if (Array.isArray(geojson)) {
          finalData = {
            type: "FeatureCollection",
            features: geojson.flatMap((fc) => {
              if (fc && fc.features) {
                return fc.features;
              } else if (fc && fc?.type === "Feature") {
                return [fc];
              } else {
                return [];
              }
            }),
          };
        } else if (
          geojson &&
          !geojson.features &&
          geojson?.type === "Feature"
        ) {
          // Single feature
          finalData = {
            type: "FeatureCollection",
            features: [geojson],
          };
        } else if (geojson && geojson?.type === "FeatureCollection") {
          // Already a proper FeatureCollection
          finalData = geojson;
        } else {
          throw new Error(
            "Unexpected shapefile structure. Expected FeatureCollection or array of features."
          );
        }

        setShapefileData(finalData);
        setOriginalShapefileData(finalData); // Store original data for reset
        setLocationOffset({ lat: 0, lng: 0 }); // Reset offset when new file is loaded

        // Fit map to shapefile bounds
        if (finalData && finalData.features && finalData.features.length > 0) {
          const bbox = turf.bbox(finalData);
          setViewState({
            longitude: (bbox[0] + bbox[2]) / 2,
            latitude: (bbox[1] + bbox[3]) / 2,
            zoom: 10,
          });
          message.success(
            `Shapefile caricato con successo! ${finalData.features.length} features trovate.`
          );
        } else {
          console.warn("No features found in shapefile");
          message.warning("Nessuna feature trovata nello shapefile");
        }
      } catch (err) {
        console.error("Error reading shapefile:", err);
        message.error(
          `Errore durante la lettura dello shapefile: ${err.message}`
        );
      }
    }

    if (fileType === "sqlite") {
      try {
        const file = info.file;
        if (!file) {
          console.error("No SQLite file provided");
          return;
        }

        if (!file.name.endsWith(".sqlite")) {
          message.error("Il file deve essere un file SQLite (.sqlite)");
          return;
        }

        setSqliteFileData(file);
        message.success("File SQLite caricato con successo!");
      } catch (err) {
        console.error("Error uploading SQLite file:", err);
        message.error(
          `Errore durante il caricamento del file SQLite: ${err.message}`
        );
      }
    }
  };

  // SQLite file props
  const sqliteProps = {
    name: "sqlite_file",
    multiple: false,
    beforeUpload: () => false, // stop automatic upload
    onChange: (info) => {
      handleFileUpload(info, "sqlite");
    },
  };

  // Shapefile props
  const shapefileProps = {
    name: "shapefile",
    multiple: false,
    beforeUpload: () => false, // stop automatic upload
    onChange: (info) => {
      handleFileUpload(info, "shapefile");
    },
  };

  // Show modal
  const showUpdateModal = () => {
    setIsUpdateModalVisible(true);
  };

  // Handle update of attributes for all features
  const handleUpdate = (values) => {
    if (shapefileData) {
      const updatedData = {
        ...shapefileData,
        features: shapefileData.features.map((feature) => ({
          ...feature,
          properties: {
            ...feature.properties,
            [values.attributeName]: values.newValue,
          },
        })),
      };
      setShapefileData(updatedData);
      setHasUnsavedChanges(true);

      // Force map re-render by updating key
      setMapKey((prev) => prev + 1);

      message.success(
        `Attributo "${values.attributeName}" aggiornato a "${values.newValue}" per tutte le features`
      );
    }
    setIsUpdateModalVisible(false);
    updateForm.resetFields();
  };

  // Handle batch update modal form submission
  const handleBatchUpdate = (values) => {
    if (selectedFeatures.length === 0 || !shapefileData) return;


    const selectedIndices = selectedFeatures.map((f) => f.index);

    const updatedData = {
      ...shapefileData,
      features: shapefileData.features.map((feature, index) => {
        if (selectedIndices.includes(index)) {
          return {
            ...feature,
            properties: {
              ...feature.properties,
              ...values, // Apply all form values
            },
          };
        }
        return feature;
      }),
    };

    setShapefileData(updatedData);

    // Update the selected features with new properties
    const updatedSelectedFeatures = selectedFeatures.map((selectedFeature) => ({
      ...selectedFeature,
      properties: {
        ...selectedFeature.properties,
        ...values,
      },
    }));
    setSelectedFeatures(updatedSelectedFeatures);

    setHasUnsavedChanges(true);

    // Force map re-render
    setMapKey((prev) => prev + 1);

    setIsBatchUpdateModalVisible(false);
    batchUpdateForm.resetFields();

    message.success(
      `Proprietà aggiornate per ${selectedFeatures.length} features`
    );
  };

  // Handle batch property update for multiple selected features
  const handleBatchPropertyUpdate = (propertyName, newValue) => {
    if (selectedFeatures.length === 0 || !shapefileData) return;


    const selectedIndices = selectedFeatures.map((f) => f.index);

    const updatedData = {
      ...shapefileData,
      features: shapefileData.features.map((feature, index) => {
        if (selectedIndices.includes(index)) {
          return {
            ...feature,
            properties: {
              ...feature.properties,
              [propertyName]: newValue,
            },
          };
        }
        return feature;
      }),
    };

    setShapefileData(updatedData);

    // Update the selected features with new properties
    const updatedSelectedFeatures = selectedFeatures.map((selectedFeature) => ({
      ...selectedFeature,
      properties: {
        ...selectedFeature.properties,
        [propertyName]: newValue,
      },
    }));
    setSelectedFeatures(updatedSelectedFeatures);

    setHasUnsavedChanges(true);

    // Force map re-render
    setMapKey((prev) => prev + 1);

    message.success(
      `Proprietà "${propertyName}" aggiornata per ${selectedFeatures.length} features`
    );
  };

  // Handle individual feature property update
  const handleFeaturePropertyUpdate = (propertyName, newValue) => {

    if (selectedFeature && shapefileData) {
      const updatedData = {
        ...shapefileData,
        features: shapefileData.features.map((feature, index) => {
          if (
            feature.properties.id === selectedFeature.properties.id ||
            index === selectedFeature.index
          ) {
            const updatedFeature = {
              ...feature,
              properties: {
                ...feature.properties,
                [propertyName]: newValue,
              },
            };
            return updatedFeature;
          }
          return feature;
        }),
      };

      setShapefileData(updatedData);

      // Update the selected feature with new properties
      const updatedSelectedFeature = {
        ...selectedFeature,
        properties: {
          ...selectedFeature.properties,
          [propertyName]: newValue,
        },
      };
      setSelectedFeature(updatedSelectedFeature);

      setFeatureProperties({
        ...featureProperties,
        [propertyName]: newValue,
      });
      setHasUnsavedChanges(true);
      message.success(
        `Proprietà "${propertyName}" aggiornata e salvata nello shapefile`
      );
    } else {
      console.error("Missing selectedFeature or shapefileData");
    }
  };

  // Add new property to selected feature
  const handleAddProperty = (values) => {
    if (selectedFeature && shapefileData) {
      const { propertyName, propertyValue } = values;

      if (featureProperties.hasOwnProperty(propertyName)) {
        message.error("Questa proprietà esiste già");
        return;
      }

      const updatedData = {
        ...shapefileData,
        features: shapefileData.features.map((feature, index) => {
          if (feature.properties.id === selectedFeature.properties.id) {
            return {
              ...feature,
              properties: {
                ...feature.properties,
                [propertyName]: propertyValue,
              },
            };
          }
          return feature;
        }),
      };
      setShapefileData(updatedData);

      // Force map re-render by updating key
      setMapKey((prev) => prev + 1);

      // Update the selected feature with new properties
      const updatedSelectedFeature = {
        ...selectedFeature,
        properties: {
          ...selectedFeature.properties,
          [propertyName]: propertyValue,
        },
      };
      setSelectedFeature(updatedSelectedFeature);

      setFeatureProperties({
        ...featureProperties,
        [propertyName]: propertyValue,
      });
      setIsAddPropertyModalVisible(false);
      newPropertyForm.resetFields();
      setHasUnsavedChanges(true);
      message.success(
        `Proprietà "${propertyName}" aggiunta e salvata nello shapefile`
      );
    }
  };

  // Delete property from selected feature
  const handleDeleteProperty = (propertyName) => {
    if (selectedFeature && shapefileData) {
      const updatedData = {
        ...shapefileData,
        features: shapefileData.features.map((feature, index) => {
          if (feature.properties.id === selectedFeature.properties.id) {
            const newProperties = { ...feature.properties };
            delete newProperties[propertyName];
            return {
              ...feature,
              properties: newProperties,
            };
          }
          return feature;
        }),
      };
      setShapefileData(updatedData);

      // Force map re-render by updating key
      setMapKey((prev) => prev + 1);

      // Update the selected feature by removing the property
      const updatedSelectedFeatureProperties = {
        ...selectedFeature.properties,
      };
      delete updatedSelectedFeatureProperties[propertyName];
      const updatedSelectedFeature = {
        ...selectedFeature,
        properties: updatedSelectedFeatureProperties,
      };
      setSelectedFeature(updatedSelectedFeature);

      const newFeatureProperties = { ...featureProperties };
      delete newFeatureProperties[propertyName];
      setFeatureProperties(newFeatureProperties);
      setHasUnsavedChanges(true);
      message.success(`Proprietà "${propertyName}" eliminata dallo shapefile`);
    }
  };

  // Cancel modal
  const handleCancel = () => {
    setIsUpdateModalVisible(false);
  };

  // Handle map click to select features
  const onMapClick = useCallback(
    (event) => {
      if (!shapefileData || !mapRef.current) return;

      try {
        const features = mapRef.current.queryRenderedFeatures(event.point, {
          layers: ["shapefile-polygons", "shapefile-lines", "shapefile-points"],
        });


        if (features.length > 0) {
          const feature = features[0];

          if (!feature.geometry) {
            console.error("Feature has no geometry");
            return;
          }

          // Try to find feature index using multiple methods
          let featureIndex = -1;

          // First try using feature id if available
          if (feature.id !== undefined) {
            featureIndex = shapefileData.features.findIndex(
              (f) => f.id === feature.id
            );
          }

          // If no id or not found, try using geometry comparison
          if (featureIndex === -1) {
            featureIndex = shapefileData.features.findIndex(
              (f) =>
                f.geometry &&
                JSON.stringify(f.geometry) === JSON.stringify(feature.geometry)
            );
          }

          // If still not found, try using properties comparison
          if (featureIndex === -1 && feature.properties) {
            featureIndex = shapefileData.features.findIndex(
              (f) =>
                f.properties &&
                JSON.stringify(f.properties) ===
                  JSON.stringify(feature.properties)
            );
          }


          // Safety check - if we couldn't find the feature index, skip
          if (featureIndex === -1) {
            console.error("Could not find feature index in shapefile data");
            message.error(
              "Errore: impossibile identificare la feature selezionata"
            );
            return;
          }

          const featureWithIndex = { ...feature, index: featureIndex };

          if (isMultiSelectMode) {
            // Multi-select mode - add/remove from selection

            setSelectedFeatures((prevSelected) => {

              const isAlreadySelected = prevSelected.some(
                (f) => f.index === featureIndex
              );

              let newSelection;
              if (isAlreadySelected) {
                // Remove from selection
                newSelection = prevSelected.filter(
                  (f) => f.index !== featureIndex
                );
              } else {
                // Add to selection
                newSelection = [...prevSelected, featureWithIndex];
              }


              return newSelection;
            });

            // Clear single selection states in multi-select mode
            setSelectedFeature(null);
            setFeatureProperties({});
            setIsDrawerVisible(false);
          } else {
            // Single select mode
            setSelectedFeatures([featureWithIndex]);
            setSelectedFeature(featureWithIndex);
            setFeatureProperties(feature.properties || {});
            setIsDrawerVisible(true);
          }
        } else {
          // Clicked on empty space
          if (!isMultiSelectMode) {
            // Only clear selection in single-select mode
            setSelectedFeatures([]);
            setSelectedFeature(null);
            setIsDrawerVisible(false);
          }
        }
      } catch (error) {
        console.error("Error in onMapClick:", error);
        message.error("Errore durante la selezione della feature");
      }
    },
    [shapefileData, isMultiSelectMode]
  );

  // Initialize map
  const onMapLoad = useCallback(() => {
  }, []);

  // Monitor shapefileData changes
  useEffect(() => {
    if (shapefileData && shapefileData.features) {
    }
  }, [shapefileData]);

  // Handle JSON generation
  const handleGenerateJSON = async () => {
    if (!shapefileData || !sqliteFileData) {
      message.error(
        "Prima di generare il JSON, carica sia lo shapefile che il file SQLite"
      );
      return;
    }

    setIsGeneratingJSON(true);
    try {
      message.loading("Generazione JSON in corso...", 0);


      // Create shapefile zip from current data - files at root level
      const options = {
        // Remove folder property to put files at root level of zip
        filename: "shapefile",
        outputType: "blob",
        compression: "DEFLATE",
        types: {
          point: "points",
          polygon: "polygons",
          polyline: "lines",
          linestring: "lines",
          multipoint: "points",
          multipolygon: "polygons",
          multilinestring: "lines",
        },
      };


      let shapefileZip;
      try {
        shapefileZip = await shpwrite.zip(shapefileData, options);
      } catch (error) {
        console.warn(
          "Shapefile generation with type mapping failed, trying fallback:",
          error
        );
        // Fallback: try without type mapping
        const fallbackOptions = {
          filename: "shapefile",
          outputType: "blob",
          compression: "DEFLATE",
        };
        shapefileZip = await shpwrite.zip(shapefileData, fallbackOptions);
      }


      // Call the API
      const response = await generateJSON(
        shapefileZip,
        sqliteFileData,
        renameScenarios,
        vehicleLengths
      );

      message.destroy();
      message.success("JSON generato con successo!");

      // You can handle the response here (e.g., download the file, display results, etc.)
      if (response.data) {
        // If the response contains a downloadable file, trigger download
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: "application/json",
        });
        FileSaver.saveAs(blob, "generated_output.json");
      }
    } catch (error) {
      message.destroy();
      console.error("Error generating JSON:", error);
      message.error(`Errore durante la generazione del JSON: ${error.message}`);
    } finally {
      setIsGeneratingJSON(false);
    }
  };

  // Show JSON Generator modal
  const showJsonGeneratorModal = () => {
    setIsJsonGeneratorModalVisible(true);
    jsonGeneratorForm.setFieldsValue({
      renameScenarios: JSON.stringify(renameScenarios, null, 2),
      vehicleLengths: JSON.stringify(vehicleLengths, null, 2),
    });
  };

  // Handle JSON Generator form submission
  const handleJsonGeneratorFormSubmit = (values) => {
    try {
      const parsedRenameScenarios = JSON.parse(values.renameScenarios);
      const parsedVehicleLengths = JSON.parse(values.vehicleLengths);

      setRenameScenarios(parsedRenameScenarios);
      setVehicleLengths(parsedVehicleLengths);
      setIsJsonGeneratorModalVisible(false);

      message.success("Configurazione aggiornata!");
    } catch (error) {
      message.error("Errore nel parsing JSON. Verifica la sintassi.");
    }
  };

  // Export shapefile
  const exportShapefile = async () => {

    if (!shapefileData) {
      console.error("No shapefile data available for export");
      message.error("Nessun shapefile da esportare");
      return;
    }


    // Validate geometry types and log detailed information
    const geometryTypes = {};
    const invalidFeatures = [];

    shapefileData.features?.forEach((feature, index) => {
      const geomType = feature.geometry?.type;
      if (geomType) {
        geometryTypes[geomType] = (geometryTypes[geomType] || 0) + 1;
      } else {
        invalidFeatures.push(index);
      }
    });

    if (invalidFeatures.length > 0) {
      console.warn("Features with invalid/missing geometry:", invalidFeatures);
    }

    try {
      message.loading("Esportazione in corso...", 0);

      // Try different export approaches

      // First attempt: with proper geometry type mapping
      let options = {
        filename: "shapefile",
        outputType: "blob",
        compression: "DEFLATE",
        types: {
          point: "points",
          polygon: "polygons",
          polyline: "lines",
          linestring: "lines",
          multipoint: "points",
          multipolygon: "polygons",
          multilinestring: "lines",
        },
      };

      let zipContent;
      try {
        zipContent = await shpwrite.zip(shapefileData, options);
      } catch (error) {
        console.warn(
          "First export attempt failed, trying without type mapping:",
          error
        );
        // Fallback: export without type mapping to ensure all features are included
        options = {
          filename: "shapefile",
          outputType: "blob",
          compression: "DEFLATE",
        };
        zipContent = await shpwrite.zip(shapefileData, options);
      }


      FileSaver.saveAs(zipContent, "modified_shapefile.zip");
      setHasUnsavedChanges(false);
      message.destroy();
      message.success(
        "Shapefile con tutte le modifiche esportato con successo!"
      );
    } catch (error) {
      message.destroy();
      console.error("Error exporting shapefile:", error);
      console.error("Error details:", error.stack);
      message.error(`Errore durante l'esportazione: ${error.message}`);
    }
  };

  return (
    <div className="upload-container">
      <script src="https://unpkg.com/shp-write@latest/shpwrite.js"></script>

      <h1 className="title">Carica e Gestisci Dati SimFix 🗺️</h1>
      <p className="description">
        Carica i tuoi file SQLite e Shapefile. Dopo il caricamento di uno
        shapefile, potrai modificarne gli attributi e visualizzarlo sulla mappa.
      </p>

      {/* Uploaders */}
      <div className="uploader-section">
        <div className="uploader-card">
          <h2>Carica File SQLite</h2>
          <Dragger {...sqliteProps} className="custom-dragger">
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Clicca o trascina qui il tuo file SQLite
            </p>
            <p className="ant-upload-hint">Supporta un singolo file.</p>
          </Dragger>
        </div>

        <div className="uploader-card">
          <h2>Carica Shapefile (.zip)</h2>
          <Dragger {...shapefileProps} className="custom-dragger">
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Clicca o trascina qui i tuoi file Shapefile
            </p>
            <p className="ant-upload-hint">Supporta un singolo file .zip.</p>
          </Dragger>
        </div>
      </div>

      {/* Show control buttons only if shapefile loaded */}
      {shapefileData && (
        <div className="control-section">
          <Button
            type="primary"
            className="update-button"
            onClick={showUpdateModal}
          >
            Aggiorna Attributi Shapefile
          </Button>
          <Button
            type={hasUnsavedChanges ? "primary" : "default"}
            onClick={exportShapefile}
            className={
              hasUnsavedChanges
                ? "update-button export-button-modified"
                : " update-button export-button"
            }
          >
            {hasUnsavedChanges
              ? "⬇️ Esporta Shapefile Modificato"
              : "Esporta Shapefile"}
          </Button>
        </div>
      )}
      {shapefileData && (
        <div className="control-section">
          <Button
            type="default"
            onClick={showJsonGeneratorModal}
            style={{ marginLeft: "10px" }}
          >
            ⚙️ Configura JSON Generator
          </Button>

          <Button
            type="primary"
            onClick={handleGenerateJSON}
            disabled={!shapefileData || !sqliteFileData || isGeneratingJSON}
            loading={isGeneratingJSON}
            style={{
              marginLeft: "10px",
              backgroundColor:
                shapefileData && sqliteFileData && !isGeneratingJSON
                  ? "#52c41a"
                  : undefined,
              borderColor:
                shapefileData && sqliteFileData && !isGeneratingJSON
                  ? "#52c41a"
                  : undefined,
            }}
          >
            {isGeneratingJSON ? "Generazione in corso..." : "🚀 Genera JSON"}
          </Button>
        </div>
      )}
      {shapefileData && (
        <div className="control-section">
          {/* File Upload Status */}
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              backgroundColor:
                shapefileData && sqliteFileData ? "#f6ffed" : "#fff7e6",
              borderRadius: "6px",
              border:
                shapefileData && sqliteFileData
                  ? "1px solid #b7eb8f"
                  : "1px solid #ffd591",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
              📁 Stato File per JSON Generator:
            </div>
            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
              <div>
                <span
                  style={{
                    color: shapefileData ? "#52c41a" : "#faad14",
                    fontWeight: "bold",
                  }}
                >
                  {shapefileData ? "✅" : "⏳"} Shapefile:{" "}
                  {shapefileData ? "Caricato" : "In attesa"}
                </span>
              </div>
              <div>
                <span
                  style={{
                    color: sqliteFileData ? "#52c41a" : "#faad14",
                    fontWeight: "bold",
                  }}
                >
                  {sqliteFileData ? "✅" : "⏳"} SQLite:{" "}
                  {sqliteFileData ? "Caricato" : "In attesa"}
                </span>
              </div>
            </div>
            {shapefileData && sqliteFileData && !isGeneratingJSON && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#52c41a",
                  marginTop: "4px",
                  fontWeight: "bold",
                }}
              >
                🚀 Pronto per generare JSON!
              </div>
            )}
            {isGeneratingJSON && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#1677ff",
                  marginTop: "4px",
                  fontWeight: "bold",
                }}
              >
                ⏳ Generazione JSON in corso...
              </div>
            )}
          </div>
        </div>
      )}
      {/* Location Adjustment Controls */}
      {shapefileData && (
        <Card
          title="🗺️ Controlli Posizione Shapefile"
          className="location-control-card"
          style={{ margin: "20px 0" }}
        >
          <Row gutter={[16, 16]}>
            {/* Directional Controls */}
            <Col xs={24} md={12}>
              <h4>Controlli Direzionali</h4>
              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                <div style={{ marginBottom: "8px" }}>
                  <Button
                    icon={<ArrowUpOutlined />}
                    onClick={() => moveShapefile("up")}
                    size="large"
                  >
                    Su
                  </Button>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => moveShapefile("left")}
                    size="large"
                  >
                    Sinistra
                  </Button>
                  <Button
                    icon={<ArrowRightOutlined />}
                    onClick={() => moveShapefile("right")}
                    size="large"
                  >
                    Destra
                  </Button>
                </div>
                <div>
                  <Button
                    icon={<ArrowDownOutlined />}
                    onClick={() => moveShapefile("down")}
                    size="large"
                  >
                    Giù
                  </Button>
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label>Dimensione Step: </label>
                <InputNumber
                  value={stepSize}
                  onChange={setStepSize}
                  min={0.0001}
                  max={0.1}
                  step={0.0001}
                  precision={4}
                  style={{ width: "120px" }}
                />
              </div>

              <Button
                icon={<ReloadOutlined />}
                onClick={resetShapefilePosition}
                type="default"
                danger
              >
                Reset Posizione
              </Button>
            </Col>

            {/* Manual Offset Controls */}
            <Col xs={24} md={12}>
              <h4>Offset Manuale</h4>
              <div style={{ marginBottom: "16px" }}>
                <label>Offset Latitudine: </label>
                <InputNumber
                  value={locationOffset.lat}
                  onChange={(value) =>
                    applyManualOffset(value || 0, locationOffset.lng)
                  }
                  precision={6}
                  step={0.001}
                  style={{ width: "100%", marginBottom: "8px" }}
                  placeholder="0.000000"
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label>Offset Longitudine: </label>
                <InputNumber
                  value={locationOffset.lng}
                  onChange={(value) =>
                    applyManualOffset(locationOffset.lat, value || 0)
                  }
                  precision={6}
                  step={0.001}
                  style={{ width: "100%" }}
                  placeholder="0.000000"
                />
              </div>

              <div
                style={{
                  backgroundColor: "#f0f2f5",
                  padding: "12px",
                  borderRadius: "6px",
                  marginTop: "16px",
                }}
              >
                <div>
                  <strong>Offset Corrente:</strong>
                </div>
                <div>Lat: {locationOffset.lat.toFixed(6)}</div>
                <div>Lng: {locationOffset.lng.toFixed(6)}</div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* Map section only if shapefile loaded */}
      {shapefileData && (
        <div className="map-section">
          <h2>Visualizzazione Mappa</h2>
          <div className="control-section">
            <div
              className={
                hasUnsavedChanges ? "status-box-modified" : "status-box"
              }
            >
              <strong>Stato Shapefile:</strong>
              Features caricate: {shapefileData?.features?.length || 0}
              {shapefileData?.features?.[0] && (
                <div>
                  Tipo geometria: {shapefileData.features[0].geometry?.type}
                </div>
              )}
              {(locationOffset.lat !== 0 || locationOffset.lng !== 0) && (
                <div style={{ color: "#1890ff", fontWeight: "bold" }}>
                  📍 Posizione modificata: Lat({locationOffset.lat.toFixed(6)}),
                  Lng({locationOffset.lng.toFixed(6)})
                </div>
              )}
              {hasUnsavedChanges && (
                <div className="warning-text">
                  ⚠️ Modifiche salvate nello shapefile - Esporta per scaricare
                  il file aggiornato
                </div>
              )}
            </div>

            {/* Selection Info */}
            <div
              style={{
                padding: "12px",
                backgroundColor:
                  selectedFeatures.length > 0 ? "#e6f4ff" : "#f5f5f5",
                borderRadius: "6px",
                border:
                  selectedFeatures.length > 0
                    ? "1px solid #91caff"
                    : "1px solid #d9d9d9",
              }}
            >
              <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                📍 Selezione Features:
              </div>
              <div>
                {selectedFeatures.length > 0 ? (
                  <>
                    <span style={{ color: "#1677ff" }}>
                      {selectedFeatures.length} feature
                      {selectedFeatures.length > 1 ? "s" : ""} selezionate
                    </span>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "4px",
                      }}
                    >
                      💡 Usa il pulsante "Modalità Selezione Multipla" per
                      selezionare più features
                    </div>
                  </>
                ) : (
                  <>
                    <span>Nessuna feature selezionata</span>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "4px",
                      }}
                    >
                      💡 Clicca su una feature sulla mappa per selezionarla
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Map Controls */}
          <div
            style={{
              position: "relative",
              marginBottom: "12px",
              display: "flex",
              gap: "12px",
              alignItems: "center",
            }}
          >
            <Button
              type={isMultiSelectMode ? "primary" : "default"}
              onClick={() => {
                setIsMultiSelectMode(!isMultiSelectMode);
                if (!isMultiSelectMode) {
                  // Entering multi-select mode - clear current selection
                  setSelectedFeatures([]);
                  setSelectedFeature(null);
                  setIsDrawerVisible(false);
                } else {
                  // Exiting multi-select mode - clear selection
                  setSelectedFeatures([]);
                  setSelectedFeature(null);
                }
              }}
              icon={isMultiSelectMode ? "🔘" : "⭕"}
            >
              {isMultiSelectMode
                ? "Modalità Selezione Multipla (ON)"
                : "Modalità Selezione Multipla (OFF)"}
            </Button>

            {isMultiSelectMode && selectedFeatures.length > 0 && (
              <Button
                type="primary"
                size="large"
                onClick={() => setIsBatchUpdateModalVisible(true)}
                style={{
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                }}
              >
                Aggiorna {selectedFeatures.length} Feature
                {selectedFeatures.length > 1 ? "s" : ""}
              </Button>
            )}
          </div>

          <div className="map-container">
            <Map
              ref={mapRef}
              {...viewState}
              onMove={(evt) => setViewState(evt.viewState)}
              className="map-element"
              mapStyle="mapbox://styles/mapbox/streets-v12"
              mapboxAccessToken={
                "pk.eyJ1IjoiYW1pbmViZWppNDUiLCJhIjoiY21mMnhpeXJoMGNmYzJtczk2dWFnbnJ5MCJ9.zGe0MdUCueQpqJ15-q_2Hg"
              }
              onClick={onMapClick}
              onLoad={onMapLoad}
            >
              <Source id="shapefile-source" type="geojson" data={shapefileData}>
                {/* Polygon fills */}
                <Layer
                  id="shapefile-polygons"
                  type="fill"
                  filter={["==", ["geometry-type"], "Polygon"]}
                  paint={{
                    "fill-color": "#A1E340",
                    "fill-opacity": 0.65,
                  }}
                />
                {/* Polygon outlines */}
                <Layer
                  id="shapefile-polygon-outline"
                  type="line"
                  filter={["==", ["geometry-type"], "Polygon"]}
                  paint={{
                    "line-color": "#A1E340",
                    "line-width": 3,
                  }}
                />
                {/* LineString features */}
                <Layer
                  id="shapefile-lines"
                  type="line"
                  filter={["==", ["geometry-type"], "LineString"]}
                  paint={{
                    "line-color": "#A1E340",
                    "line-width": 3,
                  }}
                />
                {/* Point features */}
                <Layer
                  id="shapefile-points"
                  type="circle"
                  filter={["==", ["geometry-type"], "Point"]}
                  paint={{
                    "circle-color": "#A1E340",
                    "circle-radius": 6,
                    "circle-stroke-color": "#ffffff",
                    "circle-stroke-width": 2,
                  }}
                />
              </Source>

              {/* Selected features highlighting */}
              {selectedFeatures.length > 0 && (
                <Source
                  id="selected-features-source"
                  type="geojson"
                  data={{
                    type: "FeatureCollection",
                    features: selectedFeatures,
                  }}
                >
                  {/* Selected polygon fills */}
                  <Layer
                    id="selected-polygons"
                    type="fill"
                    filter={["==", ["geometry-type"], "Polygon"]}
                    paint={{
                      "fill-color": "#ff6b35",
                      "fill-opacity": 0.4,
                    }}
                  />
                  {/* Selected polygon outlines */}
                  <Layer
                    id="selected-polygon-outline"
                    type="line"
                    filter={["==", ["geometry-type"], "Polygon"]}
                    paint={{
                      "line-color": "#ff6b35",
                      "line-width": 4,
                    }}
                  />
                  {/* Selected LineString features */}
                  <Layer
                    id="selected-lines"
                    type="line"
                    filter={["==", ["geometry-type"], "LineString"]}
                    paint={{
                      "line-color": "#ff6b35",
                      "line-width": 4,
                    }}
                  />
                  {/* Selected Point features */}
                  <Layer
                    id="selected-points"
                    type="circle"
                    filter={["==", ["geometry-type"], "Point"]}
                    paint={{
                      "circle-color": "#ff6b35",
                      "circle-radius": 8,
                      "circle-stroke-color": "#ffffff",
                      "circle-stroke-width": 3,
                    }}
                  />
                </Source>
              )}
            </Map>
          </div>
        </div>
      )}

      {/* Modal Update */}
      <Modal
        title="Aggiorna Attributo Shapefile"
        open={isUpdateModalVisible}
        onOk={() => updateForm.submit()}
        onCancel={handleCancel}
        okText="Aggiorna"
        cancelText="Annulla"
      >
        <Form form={updateForm} onFinish={handleUpdate} layout="vertical">
          <Form.Item
            name="attributeName"
            label="Nome dell'attributo"
            rules={[{ required: true, message: "Inserisci il nome!" }]}
          >
            <Input onChange={(e) => setAttributeToUpdate(e.target.value)} />
          </Form.Item>
          <Form.Item
            name="newValue"
            label="Nuovo valore"
            rules={[{ required: true, message: "Inserisci il nuovo valore!" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Property Modal */}
      <Modal
        title="Aggiungi Nuova Proprietà"
        open={isAddPropertyModalVisible}
        onOk={() => newPropertyForm.submit()}
        onCancel={() => {
          setIsAddPropertyModalVisible(false);
          newPropertyForm.resetFields();
        }}
        okText="Aggiungi"
        cancelText="Annulla"
      >
        <Form
          form={newPropertyForm}
          onFinish={handleAddProperty}
          layout="vertical"
        >
          <Form.Item
            name="propertyName"
            label="Nome Proprietà"
            rules={[
              { required: true, message: "Inserisci il nome della proprietà!" },
              {
                pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
                message:
                  "Il nome deve iniziare con una lettera e contenere solo lettere, numeri e underscore",
              },
            ]}
          >
            <Input placeholder="es. descrizione, codice, area..." />
          </Form.Item>
          <Form.Item
            name="propertyValue"
            label="Valore"
            rules={[{ required: true, message: "Inserisci il valore!" }]}
          >
            <Input placeholder="Inserisci il valore della proprietà" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Batch Update Modal */}
      <Modal
        title={`Aggiorna ${selectedFeatures.length} Feature${
          selectedFeatures.length > 1 ? "s" : ""
        }`}
        open={isBatchUpdateModalVisible}
        onOk={() => batchUpdateForm.submit()}
        onCancel={() => {
          setIsBatchUpdateModalVisible(false);
          batchUpdateForm.resetFields();
        }}
        okText="Aggiorna Tutte"
        cancelText="Annulla"
        width={600}
      >
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            backgroundColor: "#f0f2ff",
            borderRadius: "6px",
          }}
        >
          <strong>📋 Aggiornamento in Batch</strong>
          <div style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}>
            Le modifiche saranno applicate a tutte le {selectedFeatures.length}{" "}
            features selezionate. Solo i campi compilati verranno aggiornati.
          </div>
        </div>

        <Form
          form={batchUpdateForm}
          onFinish={handleBatchUpdate}
          layout="vertical"
        >
          {(() => {
            if (selectedFeatures.length === 0) return null;

            // Get all unique property keys from selected features, excluding unique identifiers
            const allPropertyKeys = new Set();
            selectedFeatures.forEach((feature) => {
              if (feature.properties) {
                Object.keys(feature.properties).forEach((key) => {
                  // Exclude common unique identifier fields
                  if (
                    ![
                      "id",
                      "ID",
                      "fid",
                      "FID",
                      "objectid",
                      "OBJECTID",
                      "gid",
                      "GID",
                    ].includes(key)
                  ) {
                    allPropertyKeys.add(key);
                  }
                });
              }
            });

            const propertyKeys = Array.from(allPropertyKeys).sort();

            if (propertyKeys.length === 0) {
              return (
                <div
                  style={{
                    textAlign: "center",
                    color: "#666",
                    padding: "20px",
                  }}
                >
                  Nessuna proprietà modificabile trovata nelle features
                  selezionate
                </div>
              );
            }

            return propertyKeys.map((key) => {
              // Get current values for this property across selected features
              const values = selectedFeatures
                .map((f) => f.properties?.[key])
                .filter((v) => v !== undefined && v !== null);

              const uniqueValues = [...new Set(values)];
              const placeholder =
                uniqueValues.length === 1
                  ? `Valore attuale: ${uniqueValues[0]}`
                  : `Valori multipli (${uniqueValues.length} diversi)`;

              return (
                <Form.Item key={key} name={key} label={key}>
                  <Input placeholder={placeholder} allowClear />
                </Form.Item>
              );
            });
          })()}
        </Form>
      </Modal>

      {/* JSON Generator Configuration Modal */}
      <Modal
        title="⚙️ Configurazione JSON Generator"
        open={isJsonGeneratorModalVisible}
        onOk={() => jsonGeneratorForm.submit()}
        onCancel={() => {
          setIsJsonGeneratorModalVisible(false);
          jsonGeneratorForm.resetFields();
        }}
        okText="Salva Configurazione"
        cancelText="Annulla"
        width={800}
      >
        <Form
          form={jsonGeneratorForm}
          onFinish={handleJsonGeneratorFormSubmit}
          layout="vertical"
        >
          <Form.Item
            name="renameScenarios"
            label="Rename Scenarios (JSON)"
            rules={[
              {
                required: true,
                message: "Inserisci la configurazione rename scenarios!",
              },
              {
                validator: (_, value) => {
                  try {
                    JSON.parse(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject("JSON non valido!");
                  }
                },
              },
            ]}
          >
            <TextArea
              rows={10}
              placeholder='{"Scenario Original": "Scenario Renamed", ...}'
            />
          </Form.Item>

          <Form.Item
            name="vehicleLengths"
            label="Vehicle Lengths (JSON)"
            rules={[
              {
                required: true,
                message: "Inserisci la configurazione vehicle lengths!",
              },
              {
                validator: (_, value) => {
                  try {
                    JSON.parse(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject("JSON non valido!");
                  }
                },
              },
            ]}
          >
            <TextArea rows={8} placeholder='{"Vehicle": length_number, ...}' />
          </Form.Item>

          <div
            style={{
              padding: "12px",
              backgroundColor: "#f0f2ff",
              borderRadius: "6px",
              marginTop: "16px",
            }}
          >
            <strong>💡 Note:</strong>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              <li>I dati devono essere in formato JSON valido</li>
              <li>
                Rename Scenarios: mappa i nomi degli scenari originali a quelli
                rinominati
              </li>
              <li>
                Vehicle Lengths: specifica la lunghezza di ogni tipo di veicolo
              </li>
              <li>La configurazione viene salvata per la sessione corrente</li>
            </ul>
          </div>
        </Form>
      </Modal>

      {/* Feature Properties Drawer */}
      <Drawer
        title={
          isMultiSelectMode
            ? `Proprietà Features (${selectedFeatures.length} selezionate)`
            : "Proprietà Feature"
        }
        placement="right"
        onClose={() => {
          setIsDrawerVisible(false);
          setSelectedFeatures([]);
          setSelectedFeature(null);
          setIsMultiSelectMode(false);
        }}
        open={isDrawerVisible}
        width={400}
      >
        {isMultiSelectMode && selectedFeatures.length > 0 ? (
          <div>
            <h3>Modifica Multipla</h3>
            <p>
              <strong>Features selezionate:</strong> {selectedFeatures.length}
            </p>
            <p
              style={{ color: "#666", fontSize: "12px", marginBottom: "16px" }}
            >
              Le modifiche saranno applicate a tutte le features selezionate
            </p>

            {/* Clear selection button */}
            <Space
              direction="vertical"
              className="drawer-button-space"
              style={{ marginBottom: "16px" }}
            >
              <Button
                onClick={() => {
                  setSelectedFeatures([]);
                  setIsMultiSelectMode(false);
                  setIsDrawerVisible(false);
                }}
                block
              >
                Deseleziona Tutto
              </Button>
            </Space>

            {/* Get common properties from all selected features */}
            {(() => {
              if (selectedFeatures.length === 0) return null;

              // Find common properties across all selected features
              const firstFeatureProps = selectedFeatures[0].properties || {};
              const commonProperties = {};

              Object.keys(firstFeatureProps).forEach((key) => {
                const allHaveProperty = selectedFeatures.every(
                  (feature) =>
                    feature.properties && feature.properties.hasOwnProperty(key)
                );
                if (allHaveProperty) {
                  // Check if all features have the same value
                  const values = selectedFeatures.map((f) => f.properties[key]);
                  const uniqueValues = [...new Set(values)];
                  commonProperties[key] =
                    uniqueValues.length === 1 ? values[0] : "[Valori Multipli]";
                }
              });

              return (
                <div>
                  <h4>Proprietà Comuni:</h4>
                  {Object.keys(commonProperties).length > 0 ? (
                    Object.entries(commonProperties).map(([key, value]) => (
                      <div key={key} className="property-item">
                        <div className="property-header">
                          <strong>{key}:</strong>
                        </div>
                        <Input
                          value={
                            value === "[Valori Multipli]" ? "" : value || ""
                          }
                          placeholder={
                            value === "[Valori Multipli]"
                              ? "Valori diversi - inserisci nuovo valore"
                              : "Inserisci valore..."
                          }
                          onChange={(e) =>
                            handleBatchPropertyUpdate(key, e.target.value)
                          }
                        />
                      </div>
                    ))
                  ) : (
                    <p className="no-properties-message">
                      Nessuna proprietà comune trovata
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        ) : selectedFeature ? (
          <div>
            <h3>Informazioni Feature</h3>
            <p>
              <strong>Tipo:</strong> {selectedFeature.geometry?.type || "N/A"}
            </p>
            <h4>Proprietà:</h4>
            <Space direction="vertical" className="drawer-button-space">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsAddPropertyModalVisible(true)}
                block
              >
                Aggiungi Proprietà
              </Button>
            </Space>

            {featureProperties && Object.keys(featureProperties).length > 0 ? (
              Object.entries(featureProperties).map(([key, value]) => (
                <div key={key} className="property-item">
                  <div className="property-header">
                    <strong>{key}:</strong>
                    <Popconfirm
                      title="Eliminare questa proprietà?"
                      onConfirm={() => handleDeleteProperty(key)}
                      okText="Sì"
                      cancelText="No"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                      />
                    </Popconfirm>
                  </div>
                  <Input
                    value={value || ""}
                    onChange={(e) =>
                      handleFeaturePropertyUpdate(key, e.target.value)
                    }
                    placeholder="Inserisci valore..."
                  />
                </div>
              ))
            ) : (
              <p className="no-properties-message">
                Nessuna proprietà disponibile
              </p>
            )}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};

export default UploadSimfix;
